import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  Input,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { SortFilter } from '@enums/sortFilter';
import { LnProductFilterComponent } from '@components/ln-product-filter/ln-product-filter.component';
import { MainProductCardComponent } from '@components/cards/main-product-card/main-product-card.component';
import { LnPrimaryButtonComponent } from '@components/butons/ln-primary-button/ln-primary-button.component';
import { ProductsService } from '@core/services/products/products.service';
import { SpecialProductInterface } from '@interfaces/products/special_product_interface';
import { ProductServiceParamsInterface } from '@interfaces/products/product_service_params_interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from '@interfaces/api/api.interface';
import { GetSpecialProductsData } from '@interfaces/products/get_special_products_data';
import { SkeletonComponent } from '@components/skeleton/skeleton.component';
import { DeviceScreenFamilyService } from '@core/services/device-screen-family/device-screen-family.service';
import { NgClass } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { SelectedFilterService } from '@core/services/selected-filter/selectedFilter.service';
import { ObjectDataState } from '../../states/object-data-State';
import { SpecialProductsObjectDataStateInterface } from '@interfaces/special-products/special-products-object-data-state-interface';
import { LnEmptySearchCardComponent } from '@components/cards/ln-empty-search-card/ln-empty-search-card.component';

@Component({
  selector: 'ln-ln-special-products',
  imports: [
    LnProductFilterComponent,
    MainProductCardComponent,
    LnPrimaryButtonComponent,
    LnEmptySearchCardComponent,
    SkeletonComponent,
    NgClass,
    TranslocoPipe,
  ],
  templateUrl: './ln-special-products.component.html',
  styleUrl: './ln-special-products.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LnSpecialProductsComponent implements OnInit {
  @Input() artisanName = '';
  @Input() sellerId?: string;

  private TAG = 'LnSpecialProductsComponent';
  products: WritableSignal<SpecialProductInterface[]> = signal<SpecialProductInterface[]>([]);
  private priceFilterSelected = true;
  pageNumber = 1;
  pageSize = 24;
  showMoreProducts = signal(false);
  protected showMoreProductsButonSpinner: WritableSignal<boolean> = signal(false);
  protected gettingProducts: WritableSignal<boolean> = signal(true);
  private productService = inject(ProductsService);
  private destroyRef = inject(DestroyRef);
  private deviceScreenFamily = inject(DeviceScreenFamilyService);
  protected currentScreenSize = this.deviceScreenFamily.currentDeviceScreenFamily;
  private selectedFilterService = inject(SelectedFilterService);
  private objectDataState: ObjectDataState<SpecialProductsObjectDataStateInterface> = inject(ObjectDataState);
  selectedFilter: WritableSignal<SortFilter> = signal<SortFilter>(SortFilter.mayorPrecio);
  initialFilter: SortFilter = SortFilter.mayorPrecio;
  private restoringPreviousState = false;
  constructor() {
    effect(() => {
      const filter = this.selectedFilter();
      if (filter != this.initialFilter && !this.restoringPreviousState) {
        this.initialFilter = filter;
        this.selectPriceOrDateFilter(filter);
        this.getProducts();
      }
    });
  }

  ngOnInit(): void {
    this.restoreObjectDataState();
  }

  get titleKey(): string {
    return this.artisanName
      ? 'components.LnSpecialProductsComponent.titleWithArtisan'
      : 'components.LnSpecialProductsComponent.title';
  }

  get titleParams() {
    return this.artisanName ? { artisanName: this.artisanName } : {};
  }

  updateObjectDataState() {
    const specialProductInterface: SpecialProductsObjectDataStateInterface = {
      selectedFilter: this.selectedFilter(),
      pageNumber: this.pageNumber,
      showMoreProducts: this.showMoreProducts(),
    };
    this.objectDataState.setData(specialProductInterface);
  }

  restoreObjectDataState() {
    const specialProductData: SpecialProductsObjectDataStateInterface | undefined = this.objectDataState.getData();
    if (specialProductData) {
      this.restoringPreviousState = true;
      this.selectedFilter.set(specialProductData.selectedFilter);
      this.selectPriceOrDateFilter(this.selectedFilter());
      this.pageNumber = specialProductData.pageNumber;
      this.showMoreProducts.set(specialProductData.showMoreProducts);
    }
    this.getProducts();
    this.restoringPreviousState = false;
  }

  getProducts() {
    const querryParams: ProductServiceParamsInterface = this.setupQuerryParams();
    //si se produjo un cambio de filtro o se esta restableciendo el estado anterior de la pagina
    let usedPageSize = this.pageSize;
    if (this.pageNumber > 1) {
      querryParams.pageSize = this.pageNumber * this.pageSize;
      usedPageSize = querryParams.pageSize;
      querryParams.pageNumber = 1;
      this.products.set([]);
    }
    this.gettingProducts.set(true);
    this.showMoreProducts.set(false);
    this.productService
      .getProducts(querryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<GetSpecialProductsData>) => {
          const productos = res.data.productos;
          const total = res.data.total;
          this.products.set(productos);
          if (productos.length >= usedPageSize && usedPageSize * this.pageNumber < total) {
            this.showMoreProducts.set(true);
          } else {
            this.showMoreProducts.set(false);
          }
          this.updateObjectDataState();
          this.gettingProducts.set(false);
        },
        error: err => {
          console.log(`${this.TAG} ${err}`);
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
      });
  }

  getMoreProducts() {
    this.showMoreProductsButonSpinner.set(true);
    this.pageNumber++;
    const querryParams: ProductServiceParamsInterface = this.setupQuerryParams();
    this.productService
      .getProducts(querryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<GetSpecialProductsData>) => {
          const productos = res.data.productos;
          const total = res.data.total;
          this.products.set([...this.products(), ...productos]);
          if (productos.length >= this.pageSize && this.pageSize * this.pageNumber < total) {
            this.showMoreProducts.set(true);
          } else {
            this.showMoreProducts.set(false);
          }
          this.updateObjectDataState();
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
        error: err => {
          console.log(`${this.TAG} ${err}`);
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
      });
  }

  setupQuerryParams(): ProductServiceParamsInterface {
    const params: ProductServiceParamsInterface = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      isPiezaUnica: false,
    };

    if (this.priceFilterSelected) {
      params.priceOrder = this.selectedFilterService.getOrderBySelectedFilter(this.selectedFilter());
    } else {
      params.dateOrder = this.selectedFilterService.getOrderBySelectedFilter(this.selectedFilter());
    }

    if (this.sellerId) {
      params.sellerId = this.sellerId;
    }

    return params;
  }

  selectPriceOrDateFilter(selectedFilterValue: SortFilter) {
    this.priceFilterSelected = this.selectedFilterService.selectPriceOrDateFilter(selectedFilterValue);
  }
}
